var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import withNotifications from '../../../../../../../app/common/withNotifications';
import { useApiMutation } from '../../../../../../infra/api';
import { useRouteParams } from '../../../../../../infra/routing';
import { endpoints } from '../../../../network';
import { useEditableField } from '../../../hooks/use-editable-field';
import { useImageUpload } from '../../../hooks/use-image-upload';
import { CampaignImages } from './campaign-images';
import { store } from '../../../../../../../main';
import { getViewerIdAndKind, useSession } from '../../../../../../infra/session';
import { buildApiPayloadFromStore } from '../go-to-next-step';
import { useCampaignDetails } from '../../../hooks';
import { useFeatureFlags } from '../../../../../../infra/feature-flags';
var JustCampaignImagesContainer = function (_a) {
    var _b;
    var addNotification = _a.addNotification, 
    // AI popover coordination props
    onAiPopoverOpen = _a.onAiPopoverOpen, forceCloseAiPopover = _a.forceCloseAiPopover;
    // Helper function to extract plain text from HTML content and trim it
    var getPlainTextContent = function (content) {
        if (!content)
            return '';
        // Create a temporary div to parse HTML and get text content
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        return tempDiv.textContent || tempDiv.innerText || '';
    };
    var updateCampaignMutation = useApiMutation({ endpoint: endpoints.updateCampaignMutation });
    var campaignId = useRouteParams().campaignId;
    var campaign = useCampaignDetails(campaignId).campaign;
    var session = useSession().data;
    var _c = useEditableField('emailCoverImage'), coverImageField = _c.field, setCoverImageValue = _c.setValue;
    var _d = useEditableField('emailLogoImage'), logoImageField = _d.field, setLogoImageValue = _d.setValue;
    var subjectField = useEditableField('emailSubject').field;
    var titleField = useEditableField('emailTitle').field;
    var bodyField = useEditableField('emailBody').field;
    var viewerIdAndKind = getViewerIdAndKind(session);
    var _e = useState(false), coverActionBarVisible = _e[0], setCoverActionBarVisible = _e[1];
    var _f = useState(false), logoActionBarVisible = _f[0], setLogoActionBarVisible = _f[1];
    var _g = useState([]), imageSuggestions = _g[0], setImageSuggestions = _g[1];
    var _h = useState([]), imageUndoStack = _h[0], setImageUndoStack = _h[1];
    var buttonLogoRef = useRef();
    var engageGenAiEnabled = useFeatureFlags().engageGenAiEnabled;
    var updateCampaign = function () {
        var apiPayload = buildApiPayloadFromStore({ store: store, session: session, campaign: campaign });
        updateCampaignMutation.mutate({
            campaignId: campaignId,
            campaign: apiPayload,
            viewerId: viewerIdAndKind.id,
            viewerType: viewerIdAndKind.kind,
        });
    };
    var generateImageMutation = useApiMutation({
        endpoint: endpoints.generateCampaignImageMutation,
        options: {
            onSuccess: function (response) {
                var _a;
                // Set the generated image as the cover image
                if ((_a = response === null || response === void 0 ? void 0 : response.image) === null || _a === void 0 ? void 0 : _a.url) {
                    setCoverImageValue(response.image.url);
                    updateCampaign();
                }
            },
            onError: function (error) {
                console.error('AI image generation failed:', error);
                // Error notifications are handled automatically due to displayErrors: true in the mutation
            },
        },
    });
    // Campaign suggestions mutation for fetching image suggestions
    var campaignSuggestionsMutation = useApiMutation({
        endpoint: endpoints.campaignSuggestionsMutation,
        options: {
            onSuccess: function (response) {
                // Convert API response to format expected by AI popover
                var formattedSuggestions = [];
                if ('images' in response) {
                    // Handle image suggestions from API
                    formattedSuggestions = response.images.map(function (image) { return ({
                        label: image.displayText,
                        prompt: image.prompt,
                    }); });
                }
                setImageSuggestions(formattedSuggestions);
            },
            onError: function (error) {
                console.error('Failed to fetch image suggestions:', error);
                setImageSuggestions([]);
            },
        },
    });
    // Function to fetch image suggestions
    var fetchImageSuggestions = function () {
        if (!campaignId) {
            console.warn('Campaign ID not available for fetching suggestions');
            return;
        }
        // Pass current field values as extraContext for better image suggestions
        var extraContext = {};
        // Include non-empty text field values for context
        if (subjectField.value) {
            var plainText = getPlainTextContent(subjectField.value).trim();
            if (plainText) {
                extraContext.subject = plainText;
            }
        }
        if (titleField.value) {
            var plainText = getPlainTextContent(titleField.value).trim();
            if (plainText) {
                extraContext.title = plainText;
            }
        }
        if (bodyField.value) {
            var plainText = getPlainTextContent(bodyField.value).trim();
            if (plainText) {
                extraContext.body = plainText;
            }
        }
        campaignSuggestionsMutation.mutate({
            campaignId: campaignId,
            data: {
                kind: 'image',
                extraContext: extraContext,
            },
        });
    };
    // Calculate undo availability
    var hasPreviousImage = useMemo(function () {
        return imageUndoStack.length > 0;
    }, [imageUndoStack]);
    // Handle undo image action
    var handleUndoImage = function () {
        if (imageUndoStack.length > 0) {
            // Get the last image from the stack and remove it
            var newStack = __spreadArray([], imageUndoStack, true);
            var previousImage = newStack.pop(); // Remove and get the last image
            // Update the stack first, then set the image
            setImageUndoStack(newStack);
            if (previousImage) {
                setCoverImageValue(previousImage);
                updateCampaign();
            }
        }
    };
    // Handle generate similar image action
    var handleGenerateSimilar = function () {
        if (!campaignId || !coverImageField.value) {
            console.warn('Campaign ID or current image not available for generating similar image');
            return;
        }
        // Store current image in undo stack before generating similar
        if (coverImageField.value) {
            setImageUndoStack(function (prev) { return __spreadArray(__spreadArray([], prev, true), [coverImageField.value], false); });
        }
        generateImageMutation.mutate({
            campaignId: campaignId,
            data: {
                prompt: 'Generate similar image',
                image: coverImageField.value,
                extraContext: {
                    subject: subjectField.value ? getPlainTextContent(subjectField.value).trim() : '',
                    title: titleField.value ? getPlainTextContent(titleField.value).trim() : '',
                    body: bodyField.value ? getPlainTextContent(bodyField.value).trim() : '',
                },
            },
        });
    };
    // Initialize undo stack when AI popover opens
    var handleInitializeUndoStack = function () {
        // Start with empty stack - images will be added when AI actions happen
        setImageUndoStack([]);
    };
    // Clear undo stack when AI popover closes (new session)
    var handleClearUndoStack = function () {
        setImageUndoStack([]);
    };
    var handleAiImageGeneration = function (promptText) {
        if (!campaignId) {
            console.warn('Campaign ID not available for AI image generation');
            return;
        }
        // Store current image in undo stack before generating new one (only if in active AI session)
        if (coverImageField.value) {
            setImageUndoStack(function (prev) { return __spreadArray(__spreadArray([], prev, true), [coverImageField.value], false); });
        }
        generateImageMutation.mutate({
            campaignId: campaignId,
            data: {
                prompt: promptText,
                extraContext: {
                    subject: subjectField.value ? getPlainTextContent(subjectField.value).trim() : '',
                    title: titleField.value ? getPlainTextContent(titleField.value).trim() : '',
                    body: bodyField.value ? getPlainTextContent(bodyField.value).trim() : '',
                },
                image: coverImageField.value,
            },
        });
    };
    var selectCoverImage = useImageUpload({
        accept: ['image/png', 'image/jpeg', 'image/jpg'],
        maxSize: 1024 * 1024 * 3,
        onChange: function (image) {
            setCoverImageValue(image.base64);
            updateCampaign();
        },
        onError: function (errors) {
            addNotification({
                title: 'Error',
                message: errors.map(function (error) { return error.message; }).join('\n'),
                level: 'error',
            });
        },
        bridgeConfig: {
            nativeCallEvent: 'system://choose-media-cover-image',
            nativeResponseEvent: 'system://choose-media-success-cover-image',
        },
        automationId: 'input:backgroundImage',
    }).selectImage;
    var selectLogoImage = useImageUpload({
        accept: ['image/png', 'image/jpeg', 'image/jpg'],
        maxSize: 1024 * 1024 * 1,
        onChange: function (image) {
            setLogoImageValue(image.base64);
            if (!coverImageField.value)
                setCoverImageValue(image.base64);
            updateCampaign();
        },
        onError: function (errors) {
            addNotification({
                title: 'Error',
                message: errors.map(function (error) { return error.message; }).join('\n'),
                level: 'error',
            });
        },
        bridgeConfig: {
            nativeCallEvent: 'system://choose-media-logo-image',
            nativeResponseEvent: 'system://choose-media-success-logo-image',
        },
        automationId: 'input:logoImage',
    }).selectImage;
    var handleCoverImageClick = function () {
        if (coverImageField.value || engageGenAiEnabled) {
            setCoverActionBarVisible(true);
        }
        else {
            selectCoverImage();
        }
    };
    var handleCoverImageDeleteClick = function () {
        setCoverImageValue(null);
        setCoverActionBarVisible(false);
    };
    var handleLogoImageClick = function () {
        if (logoImageField.value) {
            setLogoActionBarVisible(true);
        }
        else {
            selectLogoImage();
        }
    };
    var handleLogoImageDeleteClick = function () {
        setLogoImageValue('');
        setLogoActionBarVisible(false);
    };
    return (_jsx(CampaignImages, { coverImage: coverImageField.value, coverImageError: (_b = coverImageField.error) === null || _b === void 0 ? void 0 : _b.message, onCoverImageClick: handleCoverImageClick, onCoverReplaceClick: selectCoverImage, onCoverDeleteClick: handleCoverImageDeleteClick, onCoverActionBarClose: function () { return setCoverActionBarVisible(false); }, onCoverUploadClick: function () { return selectCoverImage(); }, onLogoImageClick: handleLogoImageClick, onLogoReplaceClick: selectLogoImage, onLogoDeleteClick: handleLogoImageDeleteClick, onLogoActionBarClose: function () { return setLogoActionBarVisible(false); }, logoImage: logoImageField.value, logoActionBarVisible: logoActionBarVisible, coverActionBarVisible: coverActionBarVisible, buttonLogoRef: buttonLogoRef, onAiImageGeneration: handleAiImageGeneration, imageSuggestions: imageSuggestions, onFetchImageSuggestions: fetchImageSuggestions, isGeneratingImage: generateImageMutation.isLoading, isFetchingSuggestions: campaignSuggestionsMutation.isLoading, onUndoImage: handleUndoImage, onGenerateSimilar: handleGenerateSimilar, hasPreviousImage: hasPreviousImage, onInitializeUndoStack: handleInitializeUndoStack, onClearUndoStack: handleClearUndoStack, 
        // AI popover coordination props
        onAiPopoverOpen: onAiPopoverOpen, forceCloseAiPopover: forceCloseAiPopover }));
};
export var CampaignImagesContainer = withNotifications(JustCampaignImagesContainer);
